import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  notification, 
  Popconfirm, 
  Modal,
  Form,
  Switch,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  EyeOutlined,
  TagOutlined,
  FolderOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { newsService } from '../../services';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const NewsList = () => {
  const [loading, setLoading] = useState(false);
  const [newsList, setNewsList] = useState([]);
  const [topics, setTopics] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('news');
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // States cho News
  const [newsFilters, setNewsFilters] = useState({
    keyword: '',
    topic_id: null,
    category_id: null,
    is_featured: null,
    is_new: null,
    offset: 0,
    limit: 20
  });

  // States cho Topics
  const [topicModalVisible, setTopicModalVisible] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm] = Form.useForm();
  const [topicSearchTerm, setTopicSearchTerm] = useState('');

  // States cho Categories
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm] = Form.useForm();
  const [categorySearchTerm, setCategorySearchTerm] = useState('');

  const navigate = useNavigate();

  // Filter functions
  const filteredTopics = topics.filter(topic => 
    topic.name.toLowerCase().includes(topicSearchTerm.toLowerCase()) ||
    (topic.description && topic.description.toLowerCase().includes(topicSearchTerm.toLowerCase()))
  );

  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(categorySearchTerm.toLowerCase()))
  );

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data khi component mount
  useEffect(() => {
    loadTopics();
    loadCategories();
    loadNews();
  }, []);

  // Load lại news khi filter thay đổi
  useEffect(() => {
    if (activeTab === 'news') {
      loadNews();
    }
  }, [newsFilters, activeTab]);

  // Load danh sách tin tức
  const loadNews = async () => {
    try {
      setLoading(true);
      const response = await newsService.getAll(newsFilters);
      setNewsList(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách tin tức'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load danh sách chủ đề
  const loadTopics = async () => {
    try {
      const response = await newsService.getTopics();
      setTopics(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách chủ đề'
      });
    }
  };

  // Load danh sách danh mục
  const loadCategories = async () => {
    try {
      const response = await newsService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể tải danh sách danh mục'
      });
    }
  };

  // ===== NEWS TOPICS HANDLERS =====
  const handleAddTopic = () => {
    setEditingTopic(null);
    topicForm.resetFields();
    setTopicModalVisible(true);
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    topicForm.setFieldsValue(topic);
    setTopicModalVisible(true);
  };

  const handleSaveTopic = async (values) => {
    try {
      if (editingTopic) {
        await newsService.updateTopic(editingTopic.id, values);
        notification.success({
          message: 'Thành công',
          description: 'Cập nhật chủ đề thành công'
        });
      } else {
        await newsService.createTopic(values);
        notification.success({
          message: 'Thành công',
          description: 'Thêm chủ đề thành công'
        });
      }
      setTopicModalVisible(false);
      loadTopics();
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.response?.data?.message || 'Có lỗi xảy ra'
      });
    }
  };

  const handleDeleteTopic = async (topicId) => {
    try {
      await newsService.deleteTopic(topicId);
      notification.success({
        message: 'Thành công',
        description: 'Xóa chủ đề thành công'
      });
      loadTopics();
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.response?.data?.message || 'Không thể xóa chủ đề'
      });
    }
  };

  // ===== NEWS CATEGORIES HANDLERS =====
  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    categoryForm.setFieldsValue(category);
    setCategoryModalVisible(true);
  };

  const handleSaveCategory = async (values) => {
    try {
      if (editingCategory) {
        await newsService.updateCategory(editingCategory.id, values);
        notification.success({
          message: 'Thành công',
          description: 'Cập nhật danh mục thành công'
        });
      } else {
        await newsService.createCategory(values);
        notification.success({
          message: 'Thành công',
          description: 'Thêm danh mục thành công'
        });
      }
      setCategoryModalVisible(false);
      loadCategories();
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.response?.data?.message || 'Có lỗi xảy ra'
      });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await newsService.deleteCategory(categoryId);
      notification.success({
        message: 'Thành công',
        description: 'Xóa danh mục thành công'
      });
      loadCategories();
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error.response?.data?.message || 'Không thể xóa danh mục'
      });
    }
  };

  // Mobile card rendering functions
  const renderNewsCard = (news) => (
    <Card
      key={news.id}
      size="small"
      style={{ marginBottom: 12 }}
      actions={[
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/news/view/${news.id}`)}
        >
          Xem
        </Button>,
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => navigate(`/news/edit/${news.id}`)}
        >
          Sửa
        </Button>,
        <Popconfirm
          title="Bạn có chắc chắn muốn xóa tin tức này?"
          onConfirm={() => handleDeleteNews(news.id)}
          okText="Có"
          cancelText="Không"
        >
          <Button type="text" icon={<DeleteOutlined />} danger>
            Xóa
          </Button>
        </Popconfirm>
      ]}
    >
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ 
            fontFamily: 'monospace', 
            fontSize: '11px', 
            color: '#666',
            backgroundColor: '#f5f5f5',
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            #{news.id}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {news.is_featured && <Tag color="gold" size="small">Nổi bật</Tag>}
            {news.is_new && <Tag color="red" size="small">Mới</Tag>}
          </div>
        </div>
        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: 8 }}>{news.title}</div>
        {news.summary && (
          <div style={{ color: '#666', fontSize: '14px', marginBottom: 8 }}>
            {news.summary.length > 80 ? `${news.summary.substring(0, 80)}...` : news.summary}
          </div>
        )}
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          {news.topic_name && <Tag color="blue" size="small">{news.topic_name}</Tag>}
          {news.category_name && <Tag color="green" size="small">{news.category_name}</Tag>}
        </div>
        <span style={{ fontSize: '12px', color: '#999' }}>
          {news.created_at ? new Date(news.created_at).toLocaleDateString('vi-VN') : '-'}
        </span>
      </div>
    </Card>
  );

  const renderTopicCard = (topic) => (
    <Card
      key={topic.id}
      size="small"
      style={{ marginBottom: 12 }}
      actions={[
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEditTopic(topic)}
        >
          Sửa
        </Button>,
        <Popconfirm
          title="Bạn có chắc chắn muốn xóa chủ đề này?"
          onConfirm={() => handleDeleteTopic(topic.id)}
          okText="Có"
          cancelText="Không"
        >
          <Button type="text" icon={<DeleteOutlined />} danger>
            Xóa
          </Button>
        </Popconfirm>
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          #{topic.id}
        </span>
      </div>
      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: 8 }}>{topic.name}</div>
      {topic.description && (
        <div style={{ color: '#666', fontSize: '14px' }}>
          {topic.description}
        </div>
      )}
    </Card>
  );

  const renderCategoryCard = (category) => (
    <Card
      key={category.id}
      size="small"
      style={{ marginBottom: 12 }}
      actions={[
        <Button
          type="text"
          icon={<EditOutlined />}
          onClick={() => handleEditCategory(category)}
        >
          Sửa
        </Button>,
        <Popconfirm
          title="Bạn có chắc chắn muốn xóa danh mục này?"
          onConfirm={() => handleDeleteCategory(category.id)}
          okText="Có"
          cancelText="Không"
        >
          <Button type="text" icon={<DeleteOutlined />} danger>
            Xóa
          </Button>
        </Popconfirm>
      ]}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px', 
          color: '#666',
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          #{category.id}
        </span>
        {category.slug && <Tag color="cyan" size="small">{category.slug}</Tag>}
      </div>
      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: 8 }}>{category.name}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#666', fontSize: '14px', flex: 1 }}>
          {category.description || 'Không có mô tả'}
        </div>
        {category.parent_id && (
          <Tag size="small" style={{ marginLeft: 8 }}>
            Parent: {category.parent_id}
          </Tag>
        )}
      </div>
    </Card>
  );

  // ===== TABLE COLUMNS =====
  const newsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.summary && (
            <div style={{ color: '#666', fontSize: '12px', marginTop: 4 }}>
              {record.summary.length > 100 ? `${record.summary.substring(0, 100)}...` : record.summary}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Chủ đề',
      dataIndex: 'topic_name',
      key: 'topic_name',
      width: 120,
      render: (text) => text ? <Tag color="blue">{text}</Tag> : '-',
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name', 
      key: 'category_name',
      width: 120,
      render: (text) => text ? <Tag color="green">{text}</Tag> : '-',
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.is_featured ? <Tag color="gold">Nổi bật</Tag> : null}
          {record.is_new ? <Tag color="red">Mới</Tag> : null}
          {!record.is_featured && !record.is_new ? <Tag>Thường</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text) => text ? new Date(text).toLocaleDateString('vi-VN') : '-',
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/news/view/${record.id}`)}
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/news/edit/${record.id}`)}
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa tin tức này?"
            onConfirm={() => handleDeleteNews(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const topicsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Tên chủ đề',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditTopic(record)}
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa chủ đề này?"
            onConfirm={() => handleDeleteTopic(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const categoriesColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Tên danh mục',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (text) => text ? <Tag color="cyan">{text}</Tag> : '-',
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: 'Parent ID',
      dataIndex: 'parent_id',
      key: 'parent_id',
      width: 100,
      render: (text) => text || '-',
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditCategory(record)}
          />
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa danh mục này?"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleDeleteNews = async (newsId) => {
    try {
      await newsService.delete(newsId);
      notification.success({
        message: 'Thành công',
        description: 'Xóa tin tức thành công'
      });
      loadNews();
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa tin tức'
      });
    }
  };

  const tabItems = [
    {
      key: 'news',
      label: 'Tin tức',
      icon: <EyeOutlined />,
      children: (
        <Card>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 16,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 12 : 0
          }}>
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Danh sách tin tức</Title>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => navigate('/news/add')}
              size={isMobile ? 'middle' : 'default'}
              block={isMobile}
            >
              Thêm tin tức
            </Button>
          </div>

          {/* Bộ lọc */}
          <div style={{ 
            marginBottom: 16, 
            display: 'flex', 
            gap: isMobile ? 8 : 16, 
            flexWrap: 'wrap',
            flexDirection: 'column'
          }}>
            {/* Search box - separate row on mobile */}
            <Input
              placeholder="Tìm kiếm tin tức..."
              allowClear
              style={{ width: '100%' }}
              size={isMobile ? 'large' : 'default'}
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              onChange={(e) => {
                const value = e.target.value;
                setNewsFilters(prev => ({ ...prev, keyword: value, offset: 0 }));
              }}
            />
            
            {isMobile ? (
              // Mobile: Compact filter row with 3 columns
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <Select
                  placeholder="Chủ đề"
                  size="middle"
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ ...prev, topic_id: value, offset: 0 }))}
                >
                  {topics.map(topic => (
                    <Option key={topic.id} value={topic.id}>{topic.name}</Option>
                  ))}
                </Select>

                <Select
                  placeholder="Danh mục"
                  size="middle"
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ ...prev, category_id: value, offset: 0 }))}
                >
                  {categories.map(category => (
                    <Option key={category.id} value={category.id}>{category.name}</Option>
                  ))}
                </Select>

                <Select
                  placeholder="Trạng thái"
                  size="middle"
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ 
                    ...prev, 
                    is_featured: value === 'featured' ? 1 : value === 'new' ? null : null,
                    is_new: value === 'new' ? 1 : value === 'featured' ? null : null,
                    offset: 0 
                  }))}
                >
                  <Option value="featured">Nổi bật</Option>
                  <Option value="new">Mới</Option>
                </Select>
              </div>
            ) : (
              // Desktop: Original layout
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Select
                  placeholder="Chọn chủ đề"
                  style={{ width: 150 }}
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ ...prev, topic_id: value, offset: 0 }))}
                >
                  {topics.map(topic => (
                    <Option key={topic.id} value={topic.id}>{topic.name}</Option>
                  ))}
                </Select>

                <Select
                  placeholder="Chọn danh mục"
                  style={{ width: 150 }}
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ ...prev, category_id: value, offset: 0 }))}
                >
                  {categories.map(category => (
                    <Option key={category.id} value={category.id}>{category.name}</Option>
                  ))}
                </Select>

                <Select
                  placeholder="Nổi bật"
                  style={{ width: 120 }}
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ ...prev, is_featured: value, offset: 0 }))}
                >
                  <Option value={1}>Có</Option>
                  <Option value={0}>Không</Option>
                </Select>

                <Select
                  placeholder="Tin mới"
                  style={{ width: 120 }}
                  allowClear
                  onChange={(value) => setNewsFilters(prev => ({ ...prev, is_new: value, offset: 0 }))}
                >
                  <Option value={1}>Có</Option>
                  <Option value={0}>Không</Option>
                </Select>
              </div>
            )}
          </div>

          {isMobile ? (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Đang tải...</div>
                </div>
              ) : newsList.length > 0 ? (
                <>
                  {newsList.map(news => renderNewsCard(news))}
                  {/* Mobile Pagination */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    marginTop: 16,
                    padding: '12px 0'
                  }}>
                    <Button
                      disabled={newsFilters.offset === 0}
                      onClick={() => setNewsFilters(prev => ({ 
                        ...prev, 
                        offset: Math.max(0, prev.offset - prev.limit) 
                      }))}
                      style={{ marginRight: 8 }}
                      size="small"
                    >
                      Trước
                    </Button>
                    <span style={{ margin: '0 16px', fontSize: '14px' }}>
                      Trang {Math.floor(newsFilters.offset / newsFilters.limit) + 1}
                    </span>
                    <Button
                      disabled={newsList.length < newsFilters.limit}
                      onClick={() => setNewsFilters(prev => ({ 
                        ...prev, 
                        offset: prev.offset + prev.limit 
                      }))}
                      style={{ marginLeft: 8 }}
                      size="small"
                    >
                      Sau
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div>Không tìm thấy tin tức nào</div>
                </div>
              )}
            </div>
          ) : (
            <Table
              columns={newsColumns}
              dataSource={newsList}
              rowKey="id"
              loading={loading}
              pagination={{
                current: Math.floor(newsFilters.offset / newsFilters.limit) + 1,
                pageSize: newsFilters.limit,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Tổng ${total} tin tức`,
                onChange: (page, pageSize) => {
                  setNewsFilters(prev => ({
                    ...prev,
                    offset: (page - 1) * pageSize,
                    limit: pageSize
                  }));
                }
              }}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'topics',
      label: 'Chủ đề',
      icon: <TagOutlined />,
      children: (
        <Card>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 16,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 12 : 0
          }}>
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Quản lý chủ đề bản tin</Title>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddTopic}
              size={isMobile ? 'middle' : 'default'}
              block={isMobile}
            >
              Thêm chủ đề
            </Button>
          </div>

          {/* Search box for topics */}
          <div style={{ marginBottom: 16 }}>
            <Search
              placeholder={isMobile ? "Tìm chủ đề..." : "Tìm kiếm chủ đề..."}
              allowClear
              style={{ width: isMobile ? '100%' : 300 }}
              size={isMobile ? 'large' : 'default'}
              enterButton={!isMobile}
              value={topicSearchTerm}
              onChange={(e) => setTopicSearchTerm(e.target.value)}
              onSearch={(value) => setTopicSearchTerm(value)}
            />
          </div>

          {isMobile ? (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Đang tải...</div>
                </div>
              ) : filteredTopics.length > 0 ? (
                filteredTopics.map(topic => renderTopicCard(topic))
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div>{topicSearchTerm ? 'Không tìm thấy chủ đề nào' : 'Không có chủ đề nào'}</div>
                </div>
              )}
            </div>
          ) : (
            <Table
              columns={topicsColumns}
              dataSource={filteredTopics}
              rowKey="id"
              loading={loading}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'categories',
      label: 'Danh mục',
      icon: <FolderOutlined />,
      children: (
        <Card>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 16,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 12 : 0
          }}>
            <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Quản lý danh mục bản tin</Title>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              size={isMobile ? 'middle' : 'default'}
              block={isMobile}
            >
              Thêm danh mục
            </Button>
          </div>

          {/* Search box for categories */}
          <div style={{ marginBottom: 16 }}>
            <Search
              placeholder={isMobile ? "Tìm danh mục..." : "Tìm kiếm danh mục..."}
              allowClear
              style={{ width: isMobile ? '100%' : 300 }}
              size={isMobile ? 'large' : 'default'}
              enterButton={!isMobile}
              value={categorySearchTerm}
              onChange={(e) => setCategorySearchTerm(e.target.value)}
              onSearch={(value) => setCategorySearchTerm(value)}
            />
          </div>

          {isMobile ? (
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '14px', color: '#666' }}>Đang tải...</div>
                </div>
              ) : filteredCategories.length > 0 ? (
                filteredCategories.map(category => renderCategoryCard(category))
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <div>{categorySearchTerm ? 'Không tìm thấy danh mục nào' : 'Không có danh mục nào'}</div>
                </div>
              )}
            </div>
          ) : (
            <Table
              columns={categoriesColumns}
              dataSource={filteredCategories}
              rowKey="id"
              loading={loading}
            />
          )}
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={tabItems}
        size={isMobile ? 'small' : 'default'}
      />

      {/* Modal thêm/sửa chủ đề */}
      <Modal
        title={editingTopic ? 'Sửa chủ đề' : 'Thêm chủ đề'}
        open={topicModalVisible}
        onCancel={() => setTopicModalVisible(false)}
        onOk={() => topicForm.submit()}
        okText="Lưu"
        cancelText="Hủy"
        width={isMobile ? '90%' : 520}
        style={isMobile ? { top: 20 } : {}}
      >
        <Form
          form={topicForm}
          layout="vertical"
          onFinish={handleSaveTopic}
        >
          <Form.Item
            name="name"
            label="Tên chủ đề"
            rules={[{ required: true, message: 'Vui lòng nhập tên chủ đề' }]}
          >
            <Input placeholder="Nhập tên chủ đề" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Mô tả"
          >
            <Input.TextArea rows={3} placeholder="Nhập mô tả chủ đề" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal thêm/sửa danh mục */}
      <Modal
        title={editingCategory ? 'Sửa danh mục' : 'Thêm danh mục'}
        open={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        onOk={() => categoryForm.submit()}
        okText="Lưu"
        cancelText="Hủy"
        width={isMobile ? '90%' : 600}
        style={isMobile ? { top: 20 } : {}}
      >
        <Form
          form={categoryForm}
          layout="vertical"
          onFinish={handleSaveCategory}
        >
          <Form.Item
            name="name"
            label="Tên danh mục"
            rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
          >
            <Input placeholder="Nhập tên danh mục" />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug"
            rules={[{ required: true, message: 'Vui lòng nhập slug' }]}
          >
            <Input placeholder="Nhập slug (vd: tin-tuc-cong-nghe)" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Mô tả"
          >
            <Input.TextArea rows={3} placeholder="Nhập mô tả danh mục" />
          </Form.Item>
          <Form.Item
            name="parent_id"
            label="Danh mục cha"
          >
            <Select placeholder="Chọn danh mục cha (nếu có)" allowClear>
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NewsList;
